import { useParams, Link } from 'react-router-dom';
import { Suspense, useEffect, useState } from 'react';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from '../components/ui';
import { GameRegistry, RegisteredGame } from '../components/game';
import { loadGames } from '../services/game.service';

// Loading spinner
function GameLoading() {
  return (
    <div className="aspect-video bg-black flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-purple-500 mx-auto mb-4"></div>
        <p className="text-slate-400">Loading game...</p>
      </div>
    </div>
  );
}

export function GamePage() {
  const { gameId } = useParams<{ gameId: string }>();
  const [game, setGame] = useState<RegisteredGame | null>(null);
  const [loading, setLoading] = useState(true);
  const [gamesLoaded, setGamesLoaded] = useState(false);

  // Load all games on mount
  useEffect(() => {
    loadGames().then(() => {
      setGamesLoaded(true);
    });
  }, []);

  // Get game from registry once games are loaded
  useEffect(() => {
    if (gamesLoaded && gameId) {
      const registeredGame = GameRegistry.get(gameId);
      setGame(registeredGame || null);
      setLoading(false);
    }
  }, [gamesLoaded, gameId]);

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-16">
        <GameLoading />
      </div>
    );
  }

  if (!game || !gameId) {
    return (
      <div className="container mx-auto px-4 py-16 text-center">
        <h1 className="text-3xl font-bold text-white mb-4">Game Not Found</h1>
        <p className="text-slate-400 mb-8">
          The game you&apos;re looking for doesn&apos;t exist.
        </p>
        <Link to="/games">
          <Button>Browse Games</Button>
        </Link>
      </div>
    );
  }

  const GameComponent = game.component;
  const config = game.config;

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Breadcrumb */}
      <nav className="text-sm text-slate-400 mb-6">
        <Link to="/games" className="hover:text-white">
          Games
        </Link>
        <span className="mx-2">/</span>
        <span className="text-white">{config.title}</span>
      </nav>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Game container */}
          <Card padding="none" className="overflow-hidden">
            <Suspense fallback={<GameLoading />}>
              <GameComponent />
            </Suspense>
          </Card>

          {/* Game info */}
          <Card>
            <CardHeader>
              <CardTitle>{config.title}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-300 mb-4">{config.description}</p>
              <div className="flex flex-wrap gap-4 text-sm">
                <div>
                  <span className="text-slate-500">Category:</span>{' '}
                  <span className="text-slate-300 capitalize">
                    {config.category}
                  </span>
                </div>
                <div>
                  <span className="text-slate-500">Difficulty:</span>{' '}
                  <span className="text-slate-300 capitalize">
                    {config.difficulty}
                  </span>
                </div>
                <div>
                  <span className="text-slate-500">Play Time:</span>{' '}
                  <span className="text-slate-300">
                    {config.estimatedPlayTime}
                  </span>
                </div>
              </div>
              <div className="mt-4">
                <span className="text-slate-500">Controls:</span>
                <ul className="text-slate-300 mt-1 text-sm">
                  {config.controls.keyboard.map((control, i) => (
                    <li key={i}>{control}</li>
                  ))}
                </ul>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* High Scores - placeholder for now */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>High Scores</CardTitle>
                <Link
                  to={`/leaderboards?game=${gameId}`}
                  className="text-sm text-purple-400 hover:text-purple-300"
                >
                  View all
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-slate-500 text-sm text-center py-4">
                Sign in to track your scores!
              </p>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card>
            <CardContent className="space-y-3">
              <Link to="/games">
                <Button variant="outline" fullWidth>
                  ← Back to Games
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
